import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
} from '@mui/material';
import { getAuth } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import firebase from 'firebase/compat/app';
import pointsService from '../services/pointsService';
import cardService from '../services/cardService';
import addnoteSound from '../assets/audio/addnote.mp3';
import pocketSound from '../assets/audio/pocket.mp3';

// Import tier icons
import copperIcon from '../assets/copper.png';
import nickelIcon from '../assets/nickel.png';
import bronzeIcon from '../assets/bronze.png';
import silverIcon from '../assets/silver.png';
import goldIcon from '../assets/gold.png';
import platinumIcon from '../assets/platinum.png';

const getDb = () => firebase.firestore();

// Helper to remove emojis from note titles (for legacy notes)
const stripEmojis = (text) => {
  if (!text) return text;
  // Remove all emoji characters - comprehensive range including coin emoji
  return text.replace(/[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}]/gu, '').trim();
};

function NoteDetailsDialog({ open, note, onClose, onNoteUpdated, onShowSuccess }) {
  const [loading, setLoading] = useState(false);
  const addnoteAudioRef = useRef(null);
  const pocketAudioRef = useRef(null);
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;

  if (!note) return null;

  const isCollectible = note.noteType === 'collectible' && note.canBePickedUp;
  const isTieredNote = note.noteTier && ['copper', 'nickel', 'bronze', 'silver', 'gold', 'platinum'].includes(note.noteTier);
  const canPickup = isCollectible || isTieredNote;
  const isOwnedByUser = note.createdBy === currentUserId;
  const isStatic = note.noteType === 'static';
  const canExtend = isStatic && isOwnedByUser;

  const handlePickup = async () => {
    if (!canPickup || !currentUserId) return;

    // Play pocket sound
    if (pocketAudioRef.current) {
      pocketAudioRef.current.play().catch(err => console.log('Audio play failed:', err));
    }

    setLoading(true);
    try {
      const db = getDb();
      const noteRef = doc(db, 'tokens', note.id);

      let pointsEarned = 0;
      let cardResult = null;

      // Only award points for tiered notes (not collectibles)
      if (note.noteTier) {
        pointsEarned = await pointsService.awardTieredNotePoints(
          currentUserId,
          note.noteTier
        );
      }

      // If this is a collectible card, add it to the user's collection
      if (note.noteType === 'collectible' && note.cardId) {
        console.log('Adding card to collection:', note.cardId);
        cardResult = await cardService.addCardToCollection(currentUserId, note.cardId);
        console.log('Card added result:', cardResult);
      } else if (note.noteType === 'collectible' && !note.cardId) {
        console.warn('Collectible note missing cardId:', note);
      }

      // Mark note as picked up and deactivate it
      // Set currentHolder so it appears in the user's pocket
      await updateDoc(noteRef, {
        active: false,
        pickedUpBy: currentUserId,
        pickedUpAt: serverTimestamp(),
        currentHolder: currentUserId,
        pickupCount: (note.pickupCount || 0) + 1,
      });

      // Trigger success message via parent
      let successMessage;
      if (pointsEarned > 0) {
        successMessage = `🎉 Collected! +${pointsEarned} points`;
      } else if (cardResult) {
        const rarityEmojis = {
          COMMON: '⚪',
          UNCOMMON: '🟢',
          RARE: '🔵',
          EPIC: '🟣',
          LEGENDARY: '🟠'
        };
        const emoji = rarityEmojis[cardResult.card.rarity] || '🎴';
        if (cardResult.isNewCard) {
          successMessage = `${emoji} New Card! ${cardResult.card.name} (${cardResult.card.rarity})`;
        } else {
          successMessage = `${emoji} Card Collected! ${cardResult.card.name} (x${cardResult.totalCount})`;
        }
      } else {
        successMessage = '🎉 Collected!';
      }

      onNoteUpdated();
      onClose();

      // Show success dialog via parent callback
      if (onShowSuccess) {
        onShowSuccess(successMessage);
      }
    } catch (error) {
      console.error('Error picking up note:', error);
      alert('Failed to pick up note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendExpiry = async () => {
    if (!canExtend || !currentUserId) return;

    setLoading(true);
    try {
      const success = await pointsService.extendNoteExpiry(currentUserId, note.id);

      if (success) {
        alert('✅ Note expiry extended by 24 hours!');
        onNoteUpdated();
        onClose();
      } else {
        alert('❌ Not enough points to extend. Need 10 points.');
      }
    } catch (error) {
      console.error('Error extending expiry:', error);
      alert('Failed to extend expiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTierChip = () => {
    if (!note.noteTier) return null;

    const tierColors = {
      copper: '#B87333',
      nickel: '#B0ACA8',
      bronze: '#CD7F32',
      silver: '#C0C0C0',
      gold: '#FFD700',
      platinum: '#E5E4E2',
    };

    const tierIcons = {
      copper: copperIcon,
      nickel: nickelIcon,
      bronze: bronzeIcon,
      silver: silverIcon,
      gold: goldIcon,
      platinum: platinumIcon,
    };

    return (
      <Chip
        icon={
          <Box
            component="img"
            src={tierIcons[note.noteTier]}
            alt={note.noteTier}
            sx={{ width: 20, height: 20, objectFit: 'contain' }}
          />
        }
        label={note.noteTier.toUpperCase()}
        sx={{
          backgroundColor: tierColors[note.noteTier],
          color: 'white',
          fontWeight: 600,
          mb: 2,
          '& .MuiChip-icon': {
            marginLeft: '8px',
            color: 'white',
          },
        }}
      />
    );
  };

  const getExpiryInfo = () => {
    if (!note.expiresAt) return null;

    const expiryDate = note.expiresAt.toDate ? note.expiresAt.toDate() : new Date(note.expiresAt);
    const now = new Date();
    const timeLeft = expiryDate - now;
    const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));

    if (hoursLeft < 0) {
      return <Typography color="error">Expired</Typography>;
    }

    return (
      <Typography variant="body2" color="text.secondary">
        Expires in {hoursLeft} hours
      </Typography>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)),
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 1px,
              rgba(200, 190, 170, 0.1) 1px,
              rgba(200, 190, 170, 0.1) 2px
            )
          `,
          backgroundColor: '#f9f7f4',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        },
      }}
    >
      <DialogContent sx={{ pt: 3 }}>
        <Typography
          variant="body1"
          sx={{
            fontFamily: 'Gochi Hand, cursive',
            fontSize: '1.1rem',
            lineHeight: 1.8,
            color: '#2c2c2c',
            whiteSpace: 'pre-wrap',
          }}
        >
          {note.description}
        </Typography>

        {note.businessInfo && (
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              fontFamily: 'Gochi Hand, cursive',
              color: '#2c2c2c',
            }}
          >
            {note.businessInfo}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{ fontFamily: 'Gochi Hand, cursive' }}
        >
          CLOSE
        </Button>

        {canPickup && (
          <Button
            variant="contained"
            onClick={handlePickup}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
            sx={{
              fontFamily: 'Gochi Hand, cursive',
              backgroundColor: '#2c2c2c',
              '&:hover': {
                backgroundColor: '#1a1a1a',
              },
            }}
          >
            {loading ? 'Collecting...' : (note.pointValue > 0 ? `Pick Up (+${note.pointValue} pts)` : 'Pick Up')}
          </Button>
        )}
      </DialogActions>

      {/* Hidden audio elements */}
      <audio ref={addnoteAudioRef} src={addnoteSound} preload="auto" />
      <audio ref={pocketAudioRef} src={pocketSound} preload="auto" />
    </Dialog>
  );
}

export default NoteDetailsDialog;
